import React, { useMemo, useState } from 'react';
import { VENDOR_PRESETS, getComboKey } from '../vendorPresets';
import { MicrosoftPlanDetailModal } from './MicrosoftPlanDetailModal';
import { MicrosoftLicenceField, type LinkedProductRef } from './MicrosoftLicenceField';
import { resolvedMicrosoftSkuId } from '../microsoftLicenceLink';
import { marketplaceService } from '../../services/marketplaceService';
import type { MicrosoftLicence, TermPrices } from '../microsoftLicencePricing';
import type { VendorCombination } from '../vendorPresets';
import type { InclusionItem } from '../inclusionItem';

/** Legacy dialog edits inclusions/features as plain strings only.
 * The new ProductDetailModal handles structured items. Down-cast for the legacy editor. */
const toStringList = (v: string[] | InclusionItem[] | undefined): string[] =>
  (v ?? []).map((it: any) => (typeof it === 'string' ? it : (it?.text ?? '')));

export interface ProductDraft {
  id?: string;
  kind: 'software' | 'hardware' | 'consulting';
  // Domain/category placement for hardware buckets and overall placement - REQUIRED for procurement routing
  domain: 'software' | 'computers' | 'networking' | 'mobile-hardware' | 'consulting' | 'telstra' | 'unified-collaboration';
  name: string;
  vendorOrBrand?: string;
  description?: string;
  price?: number;
  pricingType?: 'perUser' | 'lumpSum';
  term?: number;
  billingCycle?: 'monthly' | 'annual';
  image?: string;
  colour?: string;
  /** Available colour options for mobile-hardware products (max 5). Customers select at checkout. */
  availableColours?: string[];
  capacity?: string;
  sku?: string;
  provisioningNotes?: string;
  // Legacy fields for backward compatibility during migration
  pricePerUser?: number;
  tags?: string[];
  logo?: string;
  // Distributor pricing fields
  /** Buy price from distributor (cost price) */
  buyPrice?: number;
  /** Markup percentage to calculate sell price from buy price */
  markupPercent?: number;
  /** RRP from distributor for reference */
  rrpExTax?: number;
  /** Source of the product: 'dickerdata' | 'ingram' | 'telstra-distributor' | 'manual' */
  source?: 'dickerdata' | 'ingram' | 'telstra-distributor' | 'manual';
  /** Vendor part number from the distributor feed. Ingram calls this VPN. */
  vendorPartNumber?: string;
  /** Featured flag - surfaces product in Featured page for customers */
  featured?: boolean;
  /** For Telstra domain only: mobile_voice or mobile_broadband */
  telstraProductType?: 'mobile_voice' | 'mobile_broadband';
  /** For mobile-hardware domain: device eligibility for plan attachment (DRO) */
  deviceEligibility?: 'outright_only' | 'mobile_voice' | 'mobile_broadband';
  /** Custom questions for consulting products (max 5) */
  customQuestions?: Array<{
    id: string;
    label: string;
    required: boolean;
  }>;
  /**
   * Plan inclusions/features for Telstra, software, and Microsoft products.
   * Accepts legacy `string[]` or rich `InclusionItem[]` (text + optional icon/description).
   * Renderers must use `normalizeInclusionList` from `../inclusionItem`.
   */
  planInclusions?: string[] | InclusionItem[];
  /**
   * Features for computers/hardware products (max 10).
   * Accepts legacy `string[]` or rich `InclusionItem[]`.
   */
  features?: string[] | InclusionItem[];
  /** Display order for sorting products (lower numbers appear first) */
  displayOrder?: number;
  /** Sub-category for filtering (e.g., Laptop/Desktop for computers, Router/Firewall for networking) */
  subCategory?: string;
  /** Stock on hand (read-only, synced from distributor) */
  soh?: number;
  /** Vendor preset ID (e.g. 'microsoft') — drives term/billing constraints and pricing grid */
  vendorPreset?: string;
  /** Per billing/commitment combination pricing (keyed by "billingCycle_term") */
  termPricing?: Record<string, number>;
  /** Graph `subscribedSku.skuId` of the Microsoft licence a Microsoft plan sells. */
  microsoftSkuId?: string;
  /** Included apps shown as an icon grid in the customer plan detail modal (max 12) */
  inclusionApps?: Array<{ name: string; image?: string }>;
  /**
   * Canonical attributes bag from the per-SKU enrichment blob. Read-only,
   * server-hydrated for distributor-sourced hardware so all partners selling
   * the same SKU show identical features. Null for manual products.
   */
  enrichmentAttributes?: Record<string, unknown>;
}

interface ProductDialogProps {
  open: boolean;
  draft: ProductDraft;
  onChange: (next: ProductDraft) => void;
  onClose: () => void;
  onSave: () => Promise<void> | void;
  saving?: boolean;
  saveError?: string | null;
  onOpenImagePicker?: () => void;
  onDelete?: () => void;
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
  /** The partner's products, so the Microsoft licence field can flag a licence linked twice. */
  otherProducts?: readonly LinkedProductRef[];
}

/** Maps each domain to the set of optional field groups it supports */
const DOMAIN_FIELDS: Record<string, string[]> = {
  software: ['subcategory', 'planInclusions', 'sku', 'description'],
  computers: ['subcategory', 'features', 'sku'],
  networking: ['subcategory', 'colour', 'capacity', 'sku', 'description'],
  'mobile-hardware': ['deviceEligibility', 'availableColours', 'capacity', 'sku', 'description'],
  consulting: ['customQuestions', 'description'],
  telstra: ['telstraProductType', 'planInclusions'],
  'unified-collaboration': ['subcategory', 'features', 'sku', 'description'],
};

const SUBCATEGORY_OPTIONS: Record<string, string[]> = {
  computers: ['Laptop', 'Desktop'],
  networking: ['Router', 'Firewall', 'Switch', 'Access Point'],
  'unified-collaboration': ['Headsets', 'Conferencing'],
  software: ['Productivity', 'Security'],
};

const MICROSOFT_DESCRIPTION = "Microsoft licence, ordered through your IT partner and added to your organisation's Microsoft 365.";

function savedLicencePrices(
  distributors: Record<string, { prices: Record<string, TermPrices> }>,
  defaultDistributor: string | null,
): Record<string, TermPrices> {
  return distributors[defaultDistributor || '']?.prices
    ?? distributors.telstra?.prices
    ?? Object.values(distributors)[0]?.prices
    ?? {};
}

/** Pricing type defaults and lock rules per domain */
function getPricingTypeForDomain(domain: string, currentPricingType?: string, kind?: string): { value: string; locked: boolean; hint?: string } {
  if (domain === 'mobile-hardware' || domain === 'computers' || domain === 'unified-collaboration') {
    return { value: 'lumpSum', locked: true, hint: `${domain === 'mobile-hardware' ? 'Telstra handsets' : domain === 'computers' ? 'Computers' : 'Unified Collaboration'} are always priced as lump sum` };
  }
  if (domain === 'telstra') {
    return { value: 'perUser', locked: true, hint: 'Telstra plans are per user (casual, no fixed term)' };
  }
  const defaultType = (domain === 'software' || kind === 'software') ? 'perUser' : 'lumpSum';
  return { value: currentPricingType || defaultType, locked: false };
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2 pt-2 first:pt-0">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</div>
      <div className="flex-1 border-t border-gray-200" />
    </div>
  );
}

export function ProductDialog(props: ProductDialogProps) {
  const { open, draft, onChange, onClose, onSave, saving, saveError, onOpenImagePicker, onDelete, otherProducts } = props;

  const [planDetailCombo, setPlanDetailCombo] = useState<VendorCombination | null>(null);
  const [licencePrices, setLicencePrices] = useState<Record<string, TermPrices>>({});
  const [licenceDescriptions, setLicenceDescriptions] = useState<Record<string, string>>({});

  // Track which required fields the user has interacted with so we don't fire
  // "This field is required" the moment the dialog mounts. Inline errors render
  // only when a field is touched (blurred) OR the user has clicked Save.
  const [touched, setTouched] = useState<{ name?: boolean; vendorOrBrand?: boolean }>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const showError = (field: 'name' | 'vendorOrBrand') => touched[field] || submitAttempted;

  // Reset touched/submit state every time the dialog re-opens (or the draft id changes)
  // so a fresh "Add Product" doesn't inherit error state from a previous edit session.
  React.useEffect(() => {
    if (open) {
      setTouched({});
      setSubmitAttempted(false);
    }
  }, [open, draft.id]);

  const microsoftPlan = draft.vendorPreset === 'microsoft' && (draft.kind === 'software' || draft.domain === 'software');

  React.useEffect(() => {
    if (!open || !microsoftPlan) return;
    let cancelled = false;
    marketplaceService.getPartnerMicrosoftLicencePricing()
      .then((pricing) => {
        if (cancelled) return;
        setLicencePrices(savedLicencePrices(pricing.distributors, pricing.defaultDistributor));
        setLicenceDescriptions(pricing.descriptions ?? {});
      })
      .catch(() => {
        if (!cancelled) {
          setLicencePrices({});
          setLicenceDescriptions({});
        }
      });
    return () => { cancelled = true; };
  }, [open, microsoftPlan]);

  const hasDomain = Boolean(draft.domain);
  const hasTelstraType = draft.domain !== 'telstra' || Boolean(draft.telstraProductType);
  // On a distributor-sourced product the SKU is the distributor's own part
  // number, and the nightly price/stock sync matches snapshot rows on it.
  // Editing it here silently detaches the product from its feed, so the field
  // is read-only for anything that isn't hand-created.
  const skuIsSynced = draft.source !== undefined && draft.source !== 'manual';
  const domainFields = draft.domain ? (DOMAIN_FIELDS[draft.domain] || []) : [];

  const showField = (field: string) => domainFields.includes(field);

  const canSave = useMemo(() => {
    return draft.name && (draft.vendorOrBrand || '').trim().length > 0 &&
           typeof draft.price === 'number' && draft.price >= 0 && hasDomain && hasTelstraType;
  }, [draft, hasDomain, hasTelstraType]);

  const handleSaveClick = () => {
    setSubmitAttempted(true);
    if (canSave && !saving) void onSave();
  };

  const activePreset = draft.vendorPreset ? VENDOR_PRESETS.find(p => p.id === draft.vendorPreset) : null;
  const pricingInfo = draft.domain ? getPricingTypeForDomain(draft.domain, draft.pricingType, draft.kind) : { value: 'lumpSum', locked: false };
  const showTermFields = !activePreset && pricingInfo.value !== 'lumpSum';

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={() => { if (!saving) onClose(); }} />
      <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-gray-200">
        {saving && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-white/85">
            <p className="text-sm font-medium text-slate-800">Adding licence to the catalogue…</p>
          </div>
        )}
        {/* Dialog header */}
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="text-base font-semibold text-gray-900">{draft.id ? 'Edit Product' : 'Add Product'}</div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">×</button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-auto space-y-5">

          {/* ── Section 1: Product Identity ── */}
          <SectionHeader title="Product Identity" />
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Domain */}
              <div>
                <label htmlFor="product-domain" className="block text-sm text-gray-700 mb-1">
                  Domain <span className="text-red-500">*</span>
                </label>
                <select
                  id="product-domain"
                  value={draft.domain || ''}
                  onChange={(e) => {
                    const v = e.target.value as ProductDraft['domain'];
                    const nextKind: ProductDraft['kind'] = v === 'software' ? 'software' : v === 'consulting' ? 'consulting' : 'hardware';
                    onChange({ ...draft, domain: v, kind: nextKind });
                  }}
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    !draft.domain ? 'border-red-300' : 'border-gray-200'
                  }`}
                >
                  <option value="" disabled>Select domain</option>
                  <option value="software">Software</option>
                  <option value="computers">Computers</option>
                  <option value="networking">Networking</option>
                  <option value="mobile-hardware">Telstra Handsets</option>
                  <option value="consulting">Consulting</option>
                  <option value="telstra">Telstra</option>
                  <option value="unified-collaboration">Unified Collaboration</option>
                </select>
                {!draft.domain && (
                  <p className="text-xs text-red-500 mt-1">Required for procurement routing</p>
                )}
              </div>
              {/* SKU — single consolidated instance */}
              <div>
                <label htmlFor="product-sku" className="block text-sm text-gray-700 mb-1">
                  SKU <span className="text-gray-400">(optional)</span>
                  {skuIsSynced && <span className="ml-1 text-xs text-sky-600">(synced)</span>}
                </label>
                <input
                  id="product-sku"
                  value={draft.sku || ''}
                  onChange={(e) => onChange({ ...draft, sku: e.target.value.trim() || undefined })}
                  disabled={skuIsSynced}
                  placeholder={skuIsSynced ? '' : 'e.g., ABC123'}
                  className={`w-full rounded-md border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    skuIsSynced ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-200'
                  }`}
                />
                {skuIsSynced && (
                  <p className="mt-1 text-xs text-gray-500">
                    Distributor part number — used to match price and stock updates.
                  </p>
                )}
              </div>
            </div>

            {!microsoftPlan && (
            <div>
              <label htmlFor="product-name" className="block text-sm text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input
                id="product-name"
                value={draft.name}
                onChange={(e) => onChange({ ...draft, name: e.target.value })}
                onBlur={() => setTouched(t => ({ ...t, name: true }))}
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  showError('name') && !draft.name?.trim() ? 'border-red-300' : 'border-gray-200'
                }`}
                placeholder="Enter product name"
              />
              {showError('name') && !draft.name?.trim() && (
                <p className="text-xs text-red-500 mt-1">This field is required</p>
              )}
            </div>
            )}

            {/* Vendor / Brand */}
            <div>
              <label htmlFor="product-vendor-brand" className="block text-sm text-gray-700 mb-1">
                {(draft.domain || draft.kind) === 'software' ? 'Vendor' : 'Brand'} <span className="text-red-500">*</span>
              </label>
              {(draft.domain || draft.kind) === 'software' ? (
                <div className="space-y-2">
                  <select
                    id="product-vendor-preset"
                    value={draft.vendorPreset || '_custom'}
                    onChange={(e) => {
                      const presetId = e.target.value;
                      if (presetId === '_custom') {
                        onChange({ ...draft, vendorPreset: undefined, termPricing: undefined, microsoftSkuId: undefined });
                      } else {
                        const preset = VENDOR_PRESETS.find(p => p.id === presetId);
                        if (preset) {
                          const basePrice = draft.price || 0;
                          const tp: Record<string, number> = {};
                          for (const combo of preset.validCombinations) {
                            tp[getComboKey(combo.billingCycle, combo.term)] = draft.termPricing?.[getComboKey(combo.billingCycle, combo.term)] ?? basePrice;
                          }
                          onChange({
                            ...draft,
                            vendorPreset: preset.id,
                            vendorOrBrand: preset.label,
                            term: preset.defaultTerm,
                            billingCycle: preset.defaultBillingCycle,
                            termPricing: tp,
                          });
                        }
                      }
                    }}
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !(draft.vendorOrBrand || '').trim() ? 'border-red-300' : 'border-gray-200'
                    }`}
                  >
                    {VENDOR_PRESETS.map(p => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                    <option value="_custom">Other / Custom...</option>
                  </select>
                  {!draft.vendorPreset && (
                    <input
                      id="product-vendor-brand"
                      value={draft.vendorOrBrand || ''}
                      onChange={(e) => onChange({ ...draft, vendorOrBrand: e.target.value })}
                      onBlur={() => setTouched(t => ({ ...t, vendorOrBrand: true }))}
                      className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        showError('vendorOrBrand') && !(draft.vendorOrBrand || '').trim() ? 'border-red-300' : 'border-gray-200'
                      }`}
                      placeholder="Enter vendor name"
                    />
                  )}
                  {draft.vendorPreset && (
                    <p className="text-xs text-blue-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      Billing terms and pricing locked to {draft.vendorOrBrand} licensing options
                    </p>
                  )}
                </div>
              ) : (
                <input
                  id="product-vendor-brand"
                  value={draft.vendorOrBrand || ''}
                  onChange={(e) => onChange({ ...draft, vendorOrBrand: e.target.value })}
                  onBlur={() => setTouched(t => ({ ...t, vendorOrBrand: true }))}
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    showError('vendorOrBrand') && !(draft.vendorOrBrand || '').trim() ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Enter brand name"
                />
              )}
              {showError('vendorOrBrand') && !(draft.vendorOrBrand || '').trim() && (
                <p className="text-xs text-red-500 mt-1">This field is required</p>
              )}
            </div>

            {microsoftPlan && (
              <MicrosoftLicenceField
                key={draft.id ?? 'new'}
                productId={draft.id}
                productName={draft.name}
                vendor={draft.vendorOrBrand}
                value={resolvedMicrosoftSkuId(draft)}
                onChange={(microsoftSkuId) => onChange({ ...draft, microsoftSkuId, name: microsoftSkuId ? draft.name : '' })}
                onPick={(licence: MicrosoftLicence) => {
                  const termPricing = Object.fromEntries(
                    Object.entries(licencePrices[licence.skuId] ?? {}).filter(([, price]) => price > 0),
                  );
                  const priced = Object.values(termPricing);
                  onChange({
                    ...draft,
                    microsoftSkuId: licence.skuId,
                    name: licence.name,
                    image: licence.image,
                    subCategory: licence.subCategory || draft.subCategory,
                    description: licenceDescriptions[licence.skuId] || MICROSOFT_DESCRIPTION,
                    termPricing,
                    price: termPricing.monthly_12 || termPricing.monthly_0 || termPricing.annual_12 || (priced[0] ?? 0),
                  });
                }}
                otherProducts={otherProducts}
              />
            )}
          </div>

          {/* ── Section 2: Pricing & Licensing ── */}
          <SectionHeader title="Pricing & Licensing" />
          {activePreset ? (
            /* Vendor preset pricing grid (e.g. Microsoft) */
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Billing Term</label>
                  <select disabled className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm bg-gray-100 cursor-not-allowed">
                    <option>Monthly / Annual</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Set by {draft.vendorOrBrand} licensing</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Commitment Term</label>
                  <select disabled className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm bg-gray-100 cursor-not-allowed">
                    <option>Monthly / 1-Year</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Set by {draft.vendorOrBrand} licensing</p>
                </div>
              </div>

              <div className="space-y-2">
                {activePreset.validCombinations.map(combo => {
                  const key = getComboKey(combo.billingCycle, combo.term);
                  const comboPrice = draft.termPricing?.[key] ?? 0;
                  return (
                    <div key={key}>
                      <label className="block text-sm text-gray-700 mb-1">{combo.label}</label>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={comboPrice}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            const updated = { ...(draft.termPricing || {}), [key]: val };
                            const vals = Object.values(updated).filter((v): v is number => typeof v === 'number' && v > 0);
                            const derivedPrice = vals.length > 0 ? Math.min(...vals) : 0;
                            onChange({ ...draft, termPricing: updated, price: derivedPrice });
                          }}
                          className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {activePreset.showPlanDetail && (
                          <button
                            type="button"
                            onClick={() => setPlanDetailCombo(combo)}
                            className="px-2.5 py-2 rounded-md border border-gray-200 bg-white text-sm text-blue-600 hover:bg-blue-50 hover:border-blue-200 transition-colors flex items-center gap-1"
                            title="View plan details"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="hidden sm:inline">Details</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-gray-500">Browse card price auto-set to lowest combination price</p>
            </div>
          ) : (
            /* Standard pricing */
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="product-price" className="block text-sm text-gray-700 mb-1">Price</label>
                  <input id="product-price" type="number" min="0" step="0.01" value={draft.price || 0} onChange={(e) => onChange({ ...draft, price: parseFloat(e.target.value) || 0 })} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label htmlFor="product-pricing-type" className="block text-sm text-gray-700 mb-1">Pricing Type</label>
                  <select
                    id="product-pricing-type"
                    value={pricingInfo.value}
                    onChange={(e) => {
                      const newType = e.target.value as 'perUser' | 'lumpSum';
                      onChange({
                        ...draft,
                        pricingType: newType,
                        term: newType === 'lumpSum' ? undefined : (draft.term ?? 12),
                        billingCycle: newType === 'lumpSum' ? undefined : (draft.billingCycle || 'monthly')
                      });
                    }}
                    disabled={pricingInfo.locked}
                    className={`w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      pricingInfo.locked ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="perUser">Per User</option>
                    <option value="lumpSum">Lump Sum</option>
                  </select>
                  {pricingInfo.hint && (
                    <p className="text-xs text-gray-500 mt-1">{pricingInfo.hint}</p>
                  )}
                </div>
              </div>
              {showTermFields && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="product-billing-term" className="block text-sm text-gray-700 mb-1">Billing Term</label>
                    <select id="product-billing-term" value={draft.billingCycle || 'monthly'} onChange={(e) => onChange({ ...draft, billingCycle: e.target.value as 'monthly' | 'annual' })} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="product-commitment-term" className="block text-sm text-gray-700 mb-1">Commitment Term</label>
                    <select id="product-commitment-term" value={draft.term ?? 12} onChange={(e) => onChange({ ...draft, term: parseInt(e.target.value) })} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="0">Casual</option>
                      <option value="6">6 months</option>
                      <option value="12">12 months</option>
                      <option value="18">18 months</option>
                      <option value="24">24 months</option>
                      <option value="36">36 months</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Section 3: Domain Configuration ── */}
          {draft.domain && domainFields.length > 0 && (
            <>
              <SectionHeader title="Domain Configuration" />
              <div className="space-y-3">
                {/* Telstra Product Type */}
                {showField('telstraProductType') && (
                  <div>
                    <label htmlFor="product-telstra-type" className="block text-sm text-gray-700 mb-1">
                      Telstra Product Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="product-telstra-type"
                      value={draft.telstraProductType || ''}
                      onChange={(e) => onChange({
                        ...draft,
                        telstraProductType: e.target.value as 'mobile_voice' | 'mobile_broadband'
                      })}
                      className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        !draft.telstraProductType ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      <option value="" disabled>Select product type</option>
                      <option value="mobile_voice">Mobile Voice</option>
                      <option value="mobile_broadband">Mobile Broadband</option>
                    </select>
                    {!draft.telstraProductType && (
                      <p className="text-xs text-red-500 mt-1">Required for Telstra products</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Voice = phone plans, Broadband = data-only plans (hotspots, tablets)
                    </p>
                  </div>
                )}

                {/* Device Eligibility */}
                {showField('deviceEligibility') && (
                  <div>
                    <label htmlFor="product-device-eligibility" className="block text-sm text-gray-700 mb-1">
                      Device Repayment Eligibility
                    </label>
                    <select
                      id="product-device-eligibility"
                      value={draft.deviceEligibility || 'outright_only'}
                      onChange={(e) => onChange({
                        ...draft,
                        deviceEligibility: e.target.value as 'outright_only' | 'mobile_voice' | 'mobile_broadband'
                      })}
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="outright_only">Outright Only (no plan attachment)</option>
                      <option value="mobile_voice">Eligible for Mobile Voice plans</option>
                      <option value="mobile_broadband">Eligible for Mobile Broadband plans</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Determines if this device can be attached to a Telstra plan for repayment (DRO)
                    </p>
                  </div>
                )}

                {/* Sub-category */}
                {showField('subcategory') && SUBCATEGORY_OPTIONS[draft.domain] && (
                  <div>
                    <label htmlFor="product-subcategory" className="block text-sm text-gray-700 mb-1">
                      Sub-category
                    </label>
                    <select
                      id="product-subcategory"
                      value={draft.subCategory || ''}
                      onChange={(e) => onChange({ ...draft, subCategory: e.target.value.trim() || undefined })}
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Not specified</option>
                      {SUBCATEGORY_OPTIONS[draft.domain].map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Helps customers filter products</p>
                  </div>
                )}

                {/* Colour + Capacity (networking, mobile-hardware) */}
                {(showField('colour') || showField('capacity')) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {showField('colour') && (
                      <div>
                        <label htmlFor="product-colour" className="block text-sm text-gray-700 mb-1">
                          Colour <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          id="product-colour"
                          value={draft.colour || ''}
                          onChange={(e) => onChange({ ...draft, colour: e.target.value.trim() || undefined })}
                          placeholder="e.g., Black, Silver"
                          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    {showField('capacity') && (
                      <div>
                        <label htmlFor="product-capacity" className="block text-sm text-gray-700 mb-1">
                          Capacity <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          id="product-capacity"
                          value={draft.capacity || ''}
                          onChange={(e) => onChange({ ...draft, capacity: e.target.value.trim() || undefined })}
                          placeholder={draft.domain === 'mobile-hardware' ? 'e.g., 256GB (defaults to N/A)' : 'e.g., 256GB, 16GB RAM'}
                          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {draft.domain === 'mobile-hardware' && !draft.capacity?.trim() && (
                          <p className="text-xs text-amber-600 mt-1">Will default to "N/A" if not specified</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Available Colours Editor (mobile-hardware) */}
                {showField('availableColours') && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Available Colours</label>
                        <p className="text-xs text-gray-500 mt-0.5">Add colour options for customers to select at checkout (max 5)</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const currentColours = draft.availableColours || [];
                          if (currentColours.length >= 5) return;
                          onChange({ ...draft, availableColours: [...currentColours, ''] });
                        }}
                        disabled={(draft.availableColours || []).length >= 5}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          (draft.availableColours || []).length >= 5
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        + Add Colour
                      </button>
                    </div>
                    {(draft.availableColours || []).length === 0 ? (
                      <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-300 rounded-md bg-white">
                        No colour options defined. Customers won't see a colour selection.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(draft.availableColours || []).map((colour, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-md border border-gray-200">
                            <span className="text-xs font-medium text-gray-400 w-5">{index + 1}.</span>
                            <input
                              type="text"
                              value={colour}
                              onChange={(e) => {
                                const updated = [...(draft.availableColours || [])];
                                updated[index] = e.target.value;
                                onChange({ ...draft, availableColours: updated });
                              }}
                              placeholder="e.g., Black, Silver, Space Gray"
                              className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updated = (draft.availableColours || []).filter((_, i) => i !== index);
                                onChange({ ...draft, availableColours: updated.length > 0 ? updated : undefined });
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                              title="Remove colour"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Section 4: Content ── */}
          <SectionHeader title="Content" />
          <div className="space-y-3">
            {/* Description */}
            {showField('description') && (
              <div>
                <label htmlFor="product-description" className="block text-sm text-gray-700 mb-1">Description</label>
                <textarea id="product-description" value={draft.description || ''} onChange={(e) => onChange({ ...draft, description: e.target.value })} rows={3} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Brief product description..." />
              </div>
            )}

            {/* Plan Inclusions — for Telstra, software, and Microsoft vendor preset products */}
            {(showField('planInclusions') || activePreset?.showPlanDetail) && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {draft.domain === 'telstra' ? 'Plan Inclusions' : activePreset ? 'Plan Inclusions' : 'Key Features'}
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {draft.domain === 'telstra'
                        ? 'List what\'s included in this plan (each item shown as a bullet point)'
                        : activePreset
                          ? 'List what\'s included in this plan — shown in the plan detail modal'
                          : 'List key features of this software'
                      }
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const currentInclusions = toStringList(draft.planInclusions);
                      if (currentInclusions.length >= 10) return;
                      onChange({ ...draft, planInclusions: [...currentInclusions, ''] });
                    }}
                    disabled={toStringList(draft.planInclusions).length >= 10}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      toStringList(draft.planInclusions).length >= 10
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    + Add Item
                  </button>
                </div>
                {toStringList(draft.planInclusions).length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-300 rounded-md bg-white">
                    No inclusions defined. Click "Add Item" to list plan features.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {toStringList(draft.planInclusions).map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm">•</span>
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const updated = [...toStringList(draft.planInclusions)];
                            updated[index] = e.target.value;
                            onChange({ ...draft, planInclusions: updated });
                          }}
                          placeholder={draft.domain === 'telstra'
                            ? 'e.g., Unlimited calls & SMS to standard Australian numbers'
                            : activePreset
                              ? 'e.g., Exchange Online, SharePoint, Teams'
                              : 'e.g., Cloud storage included'
                          }
                          className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = toStringList(draft.planInclusions).filter((_, i) => i !== index);
                            onChange({ ...draft, planInclusions: updated });
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          title="Remove item"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Included Apps — icon grid for Microsoft / vendor preset products */}
            {activePreset?.showPlanDetail && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Included Apps</label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Add apps with optional icon URLs — shown as an icon grid in the customer detail modal (max 12)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const current = draft.inclusionApps || [];
                      if (current.length >= 12) return;
                      onChange({ ...draft, inclusionApps: [...current, { name: '' }] });
                    }}
                    disabled={(draft.inclusionApps || []).length >= 12}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      (draft.inclusionApps || []).length >= 12
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    + Add App
                  </button>
                </div>
                {(draft.inclusionApps || []).length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-300 rounded-md bg-white">
                    No apps defined. Click "Add App" to add included applications.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(draft.inclusionApps || []).map((app, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-md border border-gray-200">
                        {app.image && (
                          <img src={app.image} alt={app.name} className="w-8 h-8 object-contain flex-shrink-0 rounded" />
                        )}
                        <input
                          type="text"
                          value={app.name}
                          onChange={(e) => {
                            const updated = [...(draft.inclusionApps || [])];
                            updated[index] = { ...app, name: e.target.value };
                            onChange({ ...draft, inclusionApps: updated });
                          }}
                          placeholder="e.g., Word, Excel, Teams"
                          className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="text"
                          value={app.image || ''}
                          onChange={(e) => {
                            const updated = [...(draft.inclusionApps || [])];
                            updated[index] = { ...app, image: e.target.value.trim() || undefined };
                            onChange({ ...draft, inclusionApps: updated });
                          }}
                          placeholder="Icon URL (optional)"
                          className="w-40 rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (draft.inclusionApps || []).filter((_, i) => i !== index);
                            onChange({ ...draft, inclusionApps: updated.length > 0 ? updated : undefined });
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          title="Remove app"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Features — for computers, unified-collaboration */}
            {showField('features') && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Features</label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      List key features of this {draft.domain === 'computers' ? 'computer' : 'product'} (each item shown as a bullet point)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const currentFeatures = toStringList(draft.features);
                      if (currentFeatures.length >= 10) return;
                      onChange({ ...draft, features: [...currentFeatures, ''] });
                    }}
                    disabled={toStringList(draft.features).length >= 10}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      toStringList(draft.features).length >= 10
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    + Add Feature
                  </button>
                </div>
                {toStringList(draft.features).length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-300 rounded-md bg-white">
                    No features defined. Click "Add Feature" to list product features.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {toStringList(draft.features).map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm">•</span>
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const updated = [...toStringList(draft.features)];
                            updated[index] = e.target.value;
                            onChange({ ...draft, features: updated });
                          }}
                          placeholder="e.g., 16GB RAM, 512GB SSD, Intel Core i7"
                          className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = toStringList(draft.features).filter((_, i) => i !== index);
                            onChange({ ...draft, features: updated });
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          title="Remove feature"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Custom Questions — consulting only */}
            {showField('customQuestions') && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Custom Questions</label>
                    <p className="text-xs text-gray-500 mt-0.5">Add questions to collect information during checkout (max 5)</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const currentQuestions = draft.customQuestions || [];
                      if (currentQuestions.length >= 5) return;
                      onChange({
                        ...draft,
                        customQuestions: [
                          ...currentQuestions,
                          { id: `q-${Date.now()}`, label: '', required: false }
                        ]
                      });
                    }}
                    disabled={(draft.customQuestions || []).length >= 5}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      (draft.customQuestions || []).length >= 5
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    + Add Question
                  </button>
                </div>
                {(draft.customQuestions || []).length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-300 rounded-md bg-white">
                    No custom questions defined. Click "Add Question" to create one.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(draft.customQuestions || []).map((question, index) => (
                      <div key={question.id} className="flex items-start gap-3 p-3 bg-white rounded-md border border-gray-200">
                        <span className="text-xs font-medium text-gray-400 mt-2 w-5">{index + 1}.</span>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={question.label}
                            onChange={(e) => {
                              const updated = [...(draft.customQuestions || [])];
                              updated[index] = { ...question, label: e.target.value };
                              onChange({ ...draft, customQuestions: updated });
                            }}
                            placeholder="Enter question (e.g., 'Number of employees')"
                            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer mt-2">
                          <input
                            type="checkbox"
                            checked={question.required}
                            onChange={(e) => {
                              const updated = [...(draft.customQuestions || [])];
                              updated[index] = { ...question, required: e.target.checked };
                              onChange({ ...draft, customQuestions: updated });
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-600">Required</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (draft.customQuestions || []).filter((_, i) => i !== index);
                            onChange({ ...draft, customQuestions: updated });
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors mt-1"
                          title="Remove question"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Section 5: Display & Media ── */}
          <SectionHeader title="Display & Media" />
          <div className="space-y-3">
            {/* Image URL */}
            <div>
              <label htmlFor="product-image-url" className="block text-sm text-gray-700 mb-1">Image URL</label>
              <div className="flex items-center gap-2">
                <input id="product-image-url" value={draft.image || ''} onChange={(e) => onChange({ ...draft, image: e.target.value })} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                {onOpenImagePicker && (
                  <button type="button" onClick={() => onOpenImagePicker()} className="px-2 py-2 rounded-md border border-gray-200 bg-white text-sm hover:bg-gray-50">Select</button>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">Use the image picker from the main view to select assets</div>
            </div>

            {/* Display Order */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="product-display-order" className="block text-sm text-gray-700 mb-1">
                  Display Order <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="product-display-order"
                  type="number"
                  min="0"
                  step="1"
                  value={draft.displayOrder ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                    onChange({ ...draft, displayOrder: value });
                  }}
                  placeholder="e.g., 1, 2, 3..."
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first. Per-category ordering.</p>
              </div>
            </div>

            {/* Provisioning Notes — not shown for computers or unified-collaboration */}
            {draft.domain !== 'computers' && draft.domain !== 'unified-collaboration' && (
              <div>
                <label htmlFor="product-provisioning-notes" className="block text-sm text-gray-700 mb-1">Provisioning notes <span className="text-gray-400">(optional)</span></label>
                <textarea id="product-provisioning-notes" value={draft.provisioningNotes || ''} onChange={(e) => onChange({ ...draft, provisioningNotes: e.target.value })} rows={3} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            )}
          </div>
        </div>

        {/* Dialog footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {draft.id && onDelete && (
              <button onClick={onDelete} className="px-3 py-2 rounded-md border border-red-200 bg-white text-sm text-red-600 hover:bg-red-50">Delete</button>
            )}
            <div className="text-xs text-gray-500">Fields validate on blur and submit</div>
            {saveError && <p className="text-xs text-red-600 max-w-xs">{saveError}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-md border border-gray-200 bg-white text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={handleSaveClick} disabled={!canSave || saving} className={`px-3 py-2 rounded-md text-sm ${canSave && !saving ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500'}`}>{saving ? 'Adding…' : 'Save'}</button>
          </div>
        </div>
      </div>

      {/* Microsoft Plan Detail Modal */}
      {planDetailCombo && activePreset && (
        <MicrosoftPlanDetailModal
          open={true}
          onClose={() => setPlanDetailCombo(null)}
          productName={draft.name || 'Untitled Product'}
          combo={planDetailCombo}
          price={draft.termPricing?.[getComboKey(planDetailCombo.billingCycle, planDetailCombo.term)] ?? 0}
          planInclusions={toStringList(draft.planInclusions).filter(s => s.trim())}
          pricingType={draft.pricingType}
        />
      )}
    </div>
  );
}

export default ProductDialog;
